import { KeyboardEventHandler, RefObject, useEffect, useRef } from 'react'

import {
  ChevronDownIcon,
  ChevronUpIcon,
  MinusIcon,
  PlusIcon,
} from '@nx/fire/assets'
import { useResizeObserver } from '@nx/hooks/use-resize-observer'

import {
  StyledAccordionButton,
  StyledAccordionContent,
  StyledAccordionItem,
  StyledAccordionPanel,
  StyledAccordionPanelInner,
  StyledAccordionTitle,
  StyledIcon,
} from './FireAccordion.styles'

interface FireAccordionItemProps {
  isExpanded: boolean
  setIsExpanded: (value: boolean) => void
  id: string
  title: string | JSX.Element
  sectionRef: RefObject<HTMLButtonElement>
  sectionKeyDown: (index: number) => KeyboardEventHandler<HTMLButtonElement>
  index: number
  children: JSX.Element | JSX.Element[]
  showDivider?: boolean
  expandIcon?: 'chevron' | 'symbols'
}

export const FireAccordionItem = ({
  isExpanded,
  setIsExpanded,
  sectionRef,
  sectionKeyDown,
  id,
  title,
  children,
  index,
  showDivider = true,
  expandIcon = 'symbols',
}: FireAccordionItemProps) => {
  const { ref, height } = useResizeObserver<HTMLDivElement>()

  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    contentRef.current
      ?.querySelectorAll('a')
      .forEach((link) => (link.tabIndex = isExpanded ? 0 : -1))
  }, [isExpanded])

  return (
    <StyledAccordionItem $showDivider={showDivider}>
      <StyledAccordionTitle>
        <StyledAccordionButton
          type="button"
          as="button"
          id={id}
          aria-expanded={isExpanded}
          aria-controls={`panel-${index + 1}`}
          onKeyDown={sectionKeyDown(index)}
          onClick={() => setIsExpanded(!isExpanded)}
          ref={sectionRef}
        >
          <span>{title}</span>
          {isExpanded ? (
            <StyledIcon
              as={expandIcon === 'symbols' ? MinusIcon : ChevronUpIcon}
            />
          ) : (
            <StyledIcon
              as={expandIcon === 'symbols' ? PlusIcon : ChevronDownIcon}
            />
          )}
        </StyledAccordionButton>
      </StyledAccordionTitle>
      <StyledAccordionPanel
        id={`panel-${index + 1}`}
        data-testid={`panel-${index + 1}`}
        aria-hidden={!isExpanded}
        $height={isExpanded ? height : 0}
      >
        <StyledAccordionPanelInner ref={ref}>
          <StyledAccordionContent ref={contentRef}>
            {children}
          </StyledAccordionContent>
        </StyledAccordionPanelInner>
      </StyledAccordionPanel>
    </StyledAccordionItem>
  )
}
